/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { makeStyles, Box } from "@material-ui/core";
import { useSelector } from "react-redux";
import { nFormatter } from "lib/utils";
import getDefaultCycle, { getCycleLabel } from "lib/utils/cycles";
import _ from "lodash";
import MainConfigurationDialogComponent from "./MainScenarioDialog";
import ScenarioCard from "../Scenario_Card/CompetitorScenarioCard";

const useStyles = makeStyles(() => ({
  cardMainScenarioContainer: {
    overflow: "hidden",
    margin: "10px 30px 10px 30px",
    border: `2px solid #e2e2e2`,
    borderRadius: "5px",
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    height: "160px",
    "&:hover": {
      boxShadow: "0px 5px 5px -3px rgba(0,0,0,0.1)",
    },
  },
}));
export default function MainScenarioCardComponent({
  setBaseCompetitorScenarioData,
  baseCompetitorKpis,
  setBaseCompetitorKpis,
  cyclesWithCompetitorScenarios,
}) {
  const classes = useStyles();
  const [
    confirmDialogOpenMainConfig,
    setConfirmDialogOpenMainConfig,
  ] = useState(false);
  const [mainScenarioData, setMainScenarioData] = useState(null);
  const selectedScenariosSlice = useSelector(
    (state) => state.scenarios.selectedScenario
  );
  const companiesSlice = useSelector((state) => state.companies);
  const scenariosSlice = useSelector((state) => state.scenarios);
  const referenceScenarioSlice = useSelector(
    (state) => state.scenarios.referenceScenario
  );

  const filtersSlice = useSelector((state) => state.filters);

  const scenarioTypesAllowed = [2, 4];

  useEffect(() => {
    if (_.isEmpty(selectedScenariosSlice)) return;

    if (
      scenarioTypesAllowed.includes(
        selectedScenariosSlice.scenario.cycle.cycle_type_id
      ) &&
      cyclesWithCompetitorScenarios.length !== 0 &&
      _.find(cyclesWithCompetitorScenarios, {
        cycle_id: selectedScenariosSlice.scenario.cycle_id,
      })
    ) {
      setMainScenarioData({
        name: selectedScenariosSlice.scenario.name,
        scenarioId: selectedScenariosSlice.scenario.id,
        cycleLabel: selectedScenariosSlice.scenario.cycle.label,
        cycleId: selectedScenariosSlice?.scenario?.cycle.id,
        cycleType: selectedScenariosSlice.scenario.cycle.cycle_type_id,
        ownerLabel: selectedScenariosSlice.scenario.owner_label,
        pricePercentageModification:
          selectedScenariosSlice.scenario.name === "Reference"
            ? 0
            : _.round(
                (100 *
                  (referenceScenarioSlice.weighted_avg_price_units -
                    selectedScenariosSlice.weighted_avg_price_units)) /
                  referenceScenarioSlice.weighted_avg_price_units
              ),
        comment:
          selectedScenariosSlice.scenario.name === "Reference"
            ? ""
            : selectedScenariosSlice.scenario.comment,
        marketShare: "",
        salesOut: selectedScenariosSlice.sales,
        sellOut: selectedScenariosSlice.units,
        margin: selectedScenariosSlice.margin,
        id: selectedScenariosSlice.id,
        priceOut: selectedScenariosSlice.weighted_avg_price_units,
      });
    } else {
      const cyclesAllowed = [];
      _.forEach(scenariosSlice.totalsByScenarioId, (scenario) => {
        if (
          scenarioTypesAllowed.includes(
            scenario.scenario.cycle.cycle_type_id
          ) &&
          !cyclesAllowed.includes(scenario.scenario.cycle.id)
        ) {
          cyclesAllowed.push(scenario.scenario.cycle);
        }
      });
      if (!cyclesAllowed) {
        return;
      }
      const closestFutureCycle = getDefaultCycle(cyclesAllowed);

      const defaultScenarioSelected = _.find(
        scenariosSlice.totalsByScenarioId,
        (scenario) =>
          scenario.scenario.scenario_type_id === 1 &&
          scenario.scenario.cycle_id === closestFutureCycle
      );
      setMainScenarioData({
        name: defaultScenarioSelected.scenario.name,
        scenarioId: defaultScenarioSelected.scenario.id,
        cycleLabel: defaultScenarioSelected.scenario.cycle.label,
        cycleId: defaultScenarioSelected?.scenario?.cycle.id,
        cycleType: defaultScenarioSelected.scenario.cycle.cycle_type_id,
        ownerLabel: defaultScenarioSelected.scenario.owner_label,
        pricePercentageModification:
          defaultScenarioSelected.scenario.name === "Reference"
            ? 0
            : _.round(
                (100 *
                  (referenceScenarioSlice.weighted_avg_price_units -
                    defaultScenarioSelected.weighted_avg_price_units)) /
                  referenceScenarioSlice.weighted_avg_price_units
              ),
        comment:
          defaultScenarioSelected.scenario.name === "Reference"
            ? ""
            : defaultScenarioSelected.scenario.comment,
        marketShare: "",
        salesOut: defaultScenarioSelected.sales,
        sellOut: defaultScenarioSelected.units,
        margin: defaultScenarioSelected.margin,
        id: defaultScenarioSelected.id,
        priceOut: defaultScenarioSelected.weighted_avg_price_units,
      });
    }
  }, [selectedScenariosSlice, cyclesWithCompetitorScenarios]);

  useEffect(() => {
    if (mainScenarioData) {
      setBaseCompetitorScenarioData(mainScenarioData);
    }
  }, [mainScenarioData, filtersSlice.filters]);

  const handleMainConfig = () => {
    setConfirmDialogOpenMainConfig(true);
  };

  const handleDialogClose = () => {
    setConfirmDialogOpenMainConfig(false);
  };

  let totalValues = mainScenarioData;
  let marketShare = baseCompetitorKpis?.baseMarketShare
    ? `${_.round(baseCompetitorKpis.baseMarketShare, 2)}%`
    : "-";
  if (baseCompetitorKpis) {
    let haveTotals = false;
    let totalMarketShare = 0;
    let baseCompetitorKpisFiltered = baseCompetitorKpis.impacted_base_scenario;
    let marketShareValuesFiltered = baseCompetitorKpis.market_share_values;
    const ourCompany = _.find(companiesSlice, { is_client_company: true });
    if (
      filtersSlice.filters.category !== "" &&
      filtersSlice.filters.category !== undefined
    ) {
      baseCompetitorKpisFiltered = _.filter(
        baseCompetitorKpis.impacted_base_scenario,
        (row) => {
          return _.includes(
            _.map(filtersSlice.filters.category, "id"),
            row.category_id
          );
        }
      );

      marketShareValuesFiltered = _.filter(
        baseCompetitorKpis.market_share_values,
        (row) => {
          return _.includes(
            _.map(filtersSlice.filters.category, "id"),
            row.category_id
          );
        }
      );

      const salesBaseFilteredTotal = _.sumBy(
        marketShareValuesFiltered,
        "sales_base"
      );
      totalMarketShare =
        (_.sumBy(
          _.filter(marketShareValuesFiltered, { company_id: ourCompany.id }),
          "sales_base"
        ) /
          salesBaseFilteredTotal) *
        100;
    } else {
      totalMarketShare = _.sumBy(
        _.filter(marketShareValuesFiltered, { company_id: ourCompany.id }),
        "ms_base"
      );
    }
    const totalsBaseScenario = _.reduce(
      baseCompetitorKpisFiltered,
      (prev, curr) => {
        haveTotals = true;
        return {
          margin: prev.margin + curr.margin_base,
          salesOut: prev.salesOut + curr.sales_base,
          sellOut: prev.sellOut + curr.units_base,
        };
      },
      {
        salesOut: 0,
        sellOut: 0,
        margin: 0,
      }
    );

    if (haveTotals) {
      marketShare = `${_.round(totalMarketShare, 2)} %`;
      totalValues = {
        ...totalValues,
        ...totalsBaseScenario,
      };
    } else {
      marketShare = "-";
      totalValues = {
        ...totalValues,
        salesOut: "-",
        sellOut: "-",
        margin: "-",
      };
    }
  } else if (filtersSlice.filters.category) {
    totalValues = {
      ...totalValues,
      salesOut: "-",
      sellOut: "-",
      margin: "-",
    };
  }

  return (
    <Box className={classes.cardMainScenarioContainer}>
      <MainConfigurationDialogComponent
        open={confirmDialogOpenMainConfig}
        handleClose={handleDialogClose}
        setMainScenarioData={setMainScenarioData}
        setBaseCompetitorScenarioData={setBaseCompetitorScenarioData}
        setBaseCompetitorKpis={setBaseCompetitorKpis}
        cyclesWithCompetitorScenarios={cyclesWithCompetitorScenarios}
      />
      <ScenarioCard
        name={mainScenarioData?.name}
        cycleLabel={mainScenarioData?.cycleLabel}
        cycleType={getCycleLabel(mainScenarioData?.cycleType)}
        ownerLabel={mainScenarioData?.ownerLabel}
        pricePercentageModification={
          mainScenarioData?.pricePercentageModification
        }
        comment={mainScenarioData?.comment}
        handleScenarioClick={handleMainConfig}
        isBaseScenario
        marketShare={marketShare}
        salesOut={totalValues?.salesOut && nFormatter(totalValues?.salesOut)}
        sellOut={totalValues?.sellOut && nFormatter(totalValues?.sellOut)}
        margin={totalValues?.margin && nFormatter(totalValues?.margin)}
      />
    </Box>
  );
}
